import { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatInr } from "@/lib/intelligenceFormat";
import type { FinanceRevenueCollectionMomRow } from "@/api/intelligence";

type Props = {
  rows: FinanceRevenueCollectionMomRow[];
};

export function RevenueCollectionMomPanel({ rows }: Props) {
  const chartData = useMemo(
    () => ({
      labels: rows.map((r) => r.label),
      datasets: [
        {
          label: "Net revenue (₹)",
          data: rows.map((r) => r.netRevenue),
          backgroundColor: "rgba(79, 70, 229, 0.75)",
        },
        {
          label: "Collected from that month’s revenue (₹)",
          data: rows.map((r) => r.collectedAgainstRevenue),
          backgroundColor: "rgba(16, 185, 129, 0.75)",
        },
      ],
    }),
    [rows]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: "bottom" as const } },
      scales: {
        x: { stacked: false },
        y: { beginAtZero: true },
      },
    }),
    []
  );

  if (!rows.length) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          No month-on-month collection data
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">
          Revenue vs collection (last 6 months)
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          For each month: net revenue billed/discharged that month, and how much of{" "}
          <em>that</em> revenue has been collected till today (not cash from older bills).
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="h-[260px]">
          <Bar data={chartData} options={options} />
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead className="text-right">Net revenue</TableHead>
                <TableHead className="text-right">Collected till date</TableHead>
                <TableHead className="text-right">Still open</TableHead>
                <TableHead className="text-right">Eff. %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.monthKey}>
                  <TableCell>{r.label}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatInr(r.netRevenue)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatInr(r.collectedAgainstRevenue)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatInr(r.outstandingAgainstRevenue)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.collectionEfficiency}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
